// Settings sync — the pure merge rules (contract .claude/qa/settings-sync-contract.md
// §3–§6). Shared test vectors: test/fixtures/settings_sync_vectors.json, run by
// settingsSync.test.ts here and by the Flutter test for
// lib/providers/settings_providers.dart. Change a rule → change the vectors →
// both suites must pass.
//
// Everything is keyed by the `user_settings` column name, so the vectors read
// the same in both languages. The stores map columns to their own fields.
//
// One line: per-field last write wins; a re-sync never overwrites a field with
// a pending or newer local edit; NULL on the server never overwrites a local
// value (the local value is pushed up instead).

export type ThemeModeValue = 'system' | 'light' | 'dark';
export type SettingValue = boolean | number | string | string[] | null;
export type SettingsMap = Record<SyncColumn, SettingValue>;

/** Columns every server has (pre-160). */
export const CORE_SYNC_COLUMNS = [
  'show_romanization',
  'free_text_enabled',
  'answer_leniency',
  'session_size',
  'voice_gender',
  'auto_cache_decoys',
  'auto_warm_on_open',
  'favourite_languages',
  'typing_drill_source_lang',
  'typing_drill_target_lang',
  'ui_locale',
] as const;

/** Live on the selfhost DB (160 teaches_opt_in, 175 difficulty_support), but
 *  read defensively: a server without them still syncs the core and 181. */
export const LIVE_OPTIONAL_COLUMNS = ['teaches_opt_in', 'difficulty_support'] as const;

/** Added by db/migration_181_settings_sync.sql (draft). NULL = not set on the
 *  account yet. Missing column → the setting stays on this device. */
export const V181_COLUMNS = ['new_words_per_session', 'theme_mode', 'show_teaching_tab'] as const;

export type SyncColumn =
  | (typeof CORE_SYNC_COLUMNS)[number]
  | (typeof LIVE_OPTIONAL_COLUMNS)[number]
  | (typeof V181_COLUMNS)[number];

export const SYNC_COLUMNS: readonly SyncColumn[] = [...CORE_SYNC_COLUMNS, ...LIVE_OPTIONAL_COLUMNS, ...V181_COLUMNS];

/** Client defaults (contract §0) — what a fresh device / a reset shows. */
export const CLIENT_DEFAULTS: Readonly<SettingsMap> = {
  show_romanization: true,
  free_text_enabled: true,
  answer_leniency: 'exact',
  session_size: 20,
  voice_gender: 'f',
  auto_cache_decoys: true,
  auto_warm_on_open: false,
  favourite_languages: [],
  typing_drill_source_lang: null,
  typing_drill_target_lang: null,
  ui_locale: null,
  teaches_opt_in: false,
  difficulty_support: true,
  new_words_per_session: 5,
  theme_mode: 'system',
  show_teaching_tab: true,
};

export const MIN_SESSION_SIZE = 5;
export const MAX_SESSION_SIZE = 100;

/**
 * Normalises one value the way the DB CHECKs accept it, or null when it is
 * not a valid value for the column (an invalid server value counts as NULL;
 * an invalid local value is never written).
 */
export function normalizeSetting(col: SyncColumn, v: unknown): SettingValue {
  if (v === undefined || v === null) return null;
  switch (col) {
    case 'show_romanization':
    case 'free_text_enabled':
    case 'auto_cache_decoys':
    case 'auto_warm_on_open':
    case 'teaches_opt_in':
    case 'difficulty_support':
    case 'show_teaching_tab':
      return typeof v === 'boolean' ? v : null;
    case 'answer_leniency':
      return v === 'exact' || v === 'accents' || v === 'typos' ? v : null;
    case 'voice_gender':
      return v === 'f' || v === 'm' ? v : null;
    case 'theme_mode':
      return v === 'system' || v === 'light' || v === 'dark' ? v : null;
    case 'session_size': {
      const n = typeof v === 'number' ? Math.round(v) : Number.NaN;
      return Number.isFinite(n) ? Math.min(MAX_SESSION_SIZE, Math.max(MIN_SESSION_SIZE, n)) : null;
    }
    case 'new_words_per_session': {
      const n = typeof v === 'number' ? Math.round(v) : Number.NaN;
      return Number.isFinite(n) && n >= 0 && n <= 100 ? n : null;
    }
    case 'favourite_languages':
      return Array.isArray(v) ? v.filter((e): e is string => typeof e === 'string') : null;
    case 'typing_drill_source_lang':
    case 'typing_drill_target_lang':
    case 'ui_locale':
      return typeof v === 'string' && v.length > 0 ? v : null;
  }
}

export interface MergeInput {
  /** The signed-in user. */
  uid: string;
  /** Whose settings the local cache holds (LS `settings.owner_uid`); null = nobody yet. */
  owner: string | null;
  /** Local values for every sync column. */
  local: SettingsMap;
  /** The server row (NULL columns as null), or null when the user has no row. */
  server: Partial<Record<SyncColumn, unknown>> | null;
  /** Columns the server doesn't have (migration not applied). */
  missing: readonly SyncColumn[];
  /** Columns with a pending/newer local edit or a failed patch (§4). */
  dirty: readonly SyncColumn[];
  /** The local ui_locale was picked while signed out (the landing switcher). */
  localeOwnerless: boolean;
  /** This device once confirmed a push of teaches_opt_in (one-time migration, §3.7). */
  teachesOptInPushed: boolean;
}

export interface MergeResult {
  /** The values to show and cache. */
  state: SettingsMap;
  /** Columns to write to the server (only existing columns). */
  patch: Partial<SettingsMap>;
  /** The cache belonged to another account and was reset to defaults first. */
  reset: boolean;
}

/** The contract's sync rules as one pure function (§3 steps 3–8, §5). */
export function mergeSettings(input: MergeInput): MergeResult {
  const missing = new Set(input.missing);
  let local: SettingsMap = { ...input.local };
  let dirty = new Set(input.dirty);
  let reset = false;

  // §5 — another account's cache: back to defaults BEFORE anything is pushed,
  // so A's favourites, leniency, voice … never land in B's row. A language
  // picked on the landing page right before signing in is the new user's.
  if (input.owner != null && input.owner !== input.uid) {
    const keepLocale = input.localeOwnerless ? local.ui_locale : null;
    local = { ...CLIENT_DEFAULTS, ui_locale: keepLocale };
    dirty = new Set();
    reset = true;
  }

  const state: SettingsMap = { ...local };
  const patch: Partial<SettingsMap> = {};
  const push = (col: SyncColumn, v: SettingValue) => {
    if (missing.has(col) || v === null) return;
    patch[col] = v;
  };

  // §3.3 — no row yet: this device's values become the account's.
  if (input.server == null) {
    for (const col of SYNC_COLUMNS) push(col, local[col]);
    return { state, patch, reset };
  }

  for (const col of SYNC_COLUMNS) {
    if (missing.has(col)) continue; // device-local until the migration lands
    const mine = local[col];
    if (dirty.has(col)) {
      // A pending or failed local edit: keep it and send it (again).
      push(col, mine);
      continue;
    }
    const theirs = normalizeSetting(col, input.server[col]);

    if (col === 'ui_locale') {
      if (theirs != null) state.ui_locale = theirs;
      else if (mine != null && input.localeOwnerless) push(col, mine);
      else state.ui_locale = null; // NULL = follow system (C8)
      continue;
    }

    if (col === 'teaches_opt_in') {
      // One-time: an old device that only ever had "I teach" locally.
      if (mine === true && theirs === false && !input.teachesOptInPushed) {
        push(col, true);
        continue;
      }
    }

    if (theirs == null) {
      push(col, mine); // NULL never overwrites a local value
      continue;
    }
    state[col] = theirs; // server wins
  }
  return { state, patch, reset };
}

/** Strips columns the server doesn't have from a patch. */
export function withoutMissing(
  fields: Partial<Record<string, unknown>>,
  missing: ReadonlySet<string>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(fields)) if (!missing.has(k)) out[k] = v;
  return out;
}
